// react
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

// store
import { apiUrl } from 'store/api';

// hooks
import { useAuth, useApp, useCareCircle, useOnboarding } from 'hooks';

// components
import {
  AppStore,
  Dialog,
  Loading as LoadingPage,
  OnboardingFlow,
} from '@karehero/llama';

// launch darkly
import { useFlags } from 'launchdarkly-react-client-sdk';

// models
import { MemberRole } from '@karehero/models';

/**
 * OnboardingMemberCreate takes the user through the onboarding flow for a care concierge.
 */
const OnboardingMemberCreate = () => {
  // hooks
  const flags = useFlags();
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setSearchParams] = useSearchParams();
  const { isApp } = useApp();
  const { isAuthenticated, isLoading } = useAuth();
  const { careCircleMember } = useCareCircle();
  const {
    introConfig,
    loadingConfig,
    feelingConfig,
    typeformConfig,
    onboardingV1Config,
    updateIsOnboarded,
  } = useOnboarding({
    typeformID: 'drlsmdBU',
  });

  // state
  const [isAppStoreOpen, setIsAppStoreOpen] = useState(!isApp);

  // methods
  // set search params
  const handleNext = useCallback(
    (configID: string, subConfigID: string) => {
      setSearchParams({ configID, subConfigID });
    },
    [setSearchParams],
  );

  // complete
  const handleComplete = useCallback(async () => {
    await updateIsOnboarded();
    navigate('/action-plan');
    window.location.reload();
  }, [updateIsOnboarded, navigate]);

  // effects
  useEffect(() => {
    if (!isAuthenticated && !isLoading) navigate('/sign-in');
  }, [navigate, isAuthenticated, isLoading]);

  // memos
  const defaultConfig = useMemo(
    () => [...introConfig, ...loadingConfig],
    [introConfig, loadingConfig],
  );

  const primaryCaregiverConfig = useMemo(
    () => [
      ...introConfig,
      ...(flags.onboardingTypeform ? typeformConfig : onboardingV1Config),
      ...feelingConfig,
      ...loadingConfig,
    ],
    [
      introConfig,
      typeformConfig,
      onboardingV1Config,
      feelingConfig,
      loadingConfig,
      flags,
    ],
  );

  const onboardingConfig = useMemo(() => {
    switch (careCircleMember?.roles[0]?.name) {
      case MemberRole.PrimaryCaregiver:
        return primaryCaregiverConfig;
      default:
        return defaultConfig;
    }
  }, [careCircleMember, defaultConfig, primaryCaregiverConfig]);

  return isAuthenticated ? (
    <>
      <OnboardingFlow
        config={onboardingConfig}
        onNext={handleNext}
        onComplete={handleComplete}
      />
      {flags.appStoreSignpost && (
        <Dialog
          variant='full'
          isOpen={isAppStoreOpen}
          setIsOpen={setIsAppStoreOpen}
        >
          <AppStore storeUrl={`${apiUrl}/v1/app-store`} />
        </Dialog>
      )}
    </>
  ) : (
    <LoadingPage />
  );
};

OnboardingMemberCreate.displayName = 'OnboardingMemberCreate';
export default OnboardingMemberCreate;
